// React & State Management
import { useCallback, useEffect, useState } from "react";

// Helpers & Navigation
import { transformRoute, useTranslate } from "helpers";
import { useNavigate } from "react-router-dom";

// API & Constants
import { getVehicleServicesAPI } from "services/api/calculations/vehicleservice/api";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  VehicleServicesRow,
  VehicleServicesSearchFormValues,
} from "services/api/calculations/vehicleservice/types";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Modal } from "ui/components/Modal";
import TabMenu from "../TabMenu/TabMenu";
import VehicleServicesEditCard from "../VehicleServicesEditCard/VehicleServicesEditCard";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NoResults } from "ui/components/NoResults";

// Hooks & Custom Components
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Routes
import { Routes } from "ui/modules/calculation/navigation";

// Types
import {
  VehicleServicesSearchPageCmp,
  VehicleServicesSearchPageProps,
} from "./types";
import {
  CALCULATION_VEHICLE_SERVICES_CREATE_PAGE_TITLE,
  CALCULATION_VEHICLE_SERVICES_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Components
import VehicleServicesSearchFilters from "./components/VehicleServicesSearchFilters/VehicleServicesSearchFilters";
import VehicleServicesSearchResults from "./components/VehicleServicesSearchResults/VehicleServicesSearchResults";

const VehicleServicesSearchPage: VehicleServicesSearchPageCmp = (
  props: VehicleServicesSearchPageProps
) => {
  const { t } = useTranslate();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleServicesRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [vehicleServicesId, setVehicleServicesId] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getVehicleServicesAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationVehicleServicesDetailPageRouter, {
        vehicleServicesId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setVehicleServicesId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: VehicleServicesSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setVehicleServicesId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_VEHICLE_SERVICES_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_VEHICLE_SERVICES_SEARCH_PAGE_TITLE.icon}
              iconColor={
                CALCULATION_VEHICLE_SERVICES_SEARCH_PAGE_TITLE.iconColor
              }
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <VehicleServicesSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.vehicleservices.search.noresults"
              )}
            />
          ) : (
            <VehicleServicesSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_VEHICLE_SERVICES_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />

          <ModalBody>
            <VehicleServicesEditCard
              vehicleServicesId={vehicleServicesId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleServicesSearchPage.route =
  Routes.CalculationVehicleServicesSearchPageRouter;

export default VehicleServicesSearchPage;
